import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Chip } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  fieldRecruiterService,
  type CreateFieldRecruiterRequest,
  type FieldRecruiter,
  type FieldRecruiterStatus,
  type UpdateFieldRecruiterRequest,
} from '../services';
import { cityService, type City } from '../services/hubService';

const STATUS_OPTIONS: FieldRecruiterStatus[] = ['ACTIVE', 'INACTIVE'];
const PAGE_SIZE = 15;
const defaultFormState: CreateFieldRecruiterRequest = {
  employeeId: '',
  name: '',
  phone: '',
  email: '',
  city: '',
  status: 'ACTIVE',
};

type FormErrors = Partial<Record<keyof CreateFieldRecruiterRequest, string>>;

const FieldRecruiterManagement: React.FC = () => {
  const [recruiters, setRecruiters] = useState<FieldRecruiter[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesLoadError, setCitiesLoadError] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [cityFilter, setCityFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<FieldRecruiterStatus | ''>('');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRecruiter, setSelectedRecruiter] = useState<FieldRecruiter | null>(null);
  const [formState, setFormState] = useState<CreateFieldRecruiterRequest>(defaultFormState);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadRecruiters = async () => {
    setLoading(true);
    try {
      const response = await fieldRecruiterService.getFieldRecruiters({
        ...(searchFilter.trim() ? { search: searchFilter.trim() } : {}),
        ...(cityFilter.length === 1 ? { city: cityFilter[0] } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        page: page + 1,
        limit: PAGE_SIZE,
      });
      const selectedCities = cityFilter.map((city) => city.trim().toLowerCase()).filter(Boolean);
      const filteredRecruiters = (response.data || []).filter((recruiter: FieldRecruiter) =>
        selectedCities.length === 0 ||
        selectedCities.some((city) => recruiter.city.toLowerCase().includes(city)),
      );
      const hasServerPagination =
        response.pagination?.page === page + 1 &&
        response.pagination?.limit === PAGE_SIZE;
      setRecruiters(
        hasServerPagination
          ? filteredRecruiters
          : filteredRecruiters.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
      );
      setTotalCount(
        hasServerPagination
          ? response.pagination?.total ?? 0
          : filteredRecruiters.length,
      );
    } catch (error) {
      console.error('Error loading field recruiters:', error);
      setSnackbar({ open: true, message: 'Failed to load recruiters.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecruiters();
  }, [page, searchFilter, cityFilter, statusFilter]);

  useEffect(() => {
    setCitiesLoading(true);
    cityService.getOperationalCities()
      .then((cityData) => {
        setCities(cityData || []);
        setCitiesLoadError(false);
      })
      .catch((error) => {
        console.error('Error loading cities:', error);
        setCitiesLoadError(true);
      })
      .finally(() => setCitiesLoading(false));
  }, []);

  const handleOpenDialog = (recruiter?: FieldRecruiter) => {
    if (recruiter) {
      setSelectedRecruiter(recruiter);
      setFormState({
        employeeId: recruiter.employeeId,
        name: recruiter.name,
        phone: recruiter.phone,
        email: recruiter.email,
        city: recruiter.city,
        status: recruiter.status,
      });
      setSelectedCities(recruiter.city ? recruiter.city.split(',').map((city) => city.trim()) : []);
      setIsEditing(true);
    } else {
      setSelectedRecruiter(null);
      setFormState(defaultFormState);
      setSelectedCities([]);
      setIsEditing(false);
    }
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedRecruiter(null);
    setFormState(defaultFormState);
    setSelectedCities([]);
    setFormErrors({});
    setIsEditing(false);
  };

  const handleFormChange = (field: keyof CreateFieldRecruiterRequest, value: string) => {
    setFormState((previous) => ({ ...previous, [field]: field === 'status' ? value as FieldRecruiterStatus : value }));
    setFormErrors((previous) => ({ ...previous, [field]: undefined }));
  };

  const handleCityChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const citiesValue = typeof value === 'string' ? value.split(',') : value;
    setSelectedCities(citiesValue);
    handleFormChange('city', citiesValue.join(', '));
  };

  const validateForm = () => {
    const errors: FormErrors = {};
    if (!formState.employeeId.trim()) errors.employeeId = 'Employee ID is required.';
    if (!formState.name.trim()) errors.name = 'Name is required.';
    else if (formState.name.trim().length < 2) errors.name = 'Name must be at least 2 characters.';
    if (!formState.phone.trim()) errors.phone = 'Phone is required.';
    else if (!/^[0-9]{10}$/.test(formState.phone.trim())) errors.phone = 'Phone must be a valid 10-digit number.';
    if (!formState.email.trim()) errors.email = 'Email is required.';
    else if (!/^[^\s@]+@ev91riderz\.com$/i.test(formState.email.trim())) errors.email = 'Email must be a valid EV91 email (@ev91riderz.com).';
    if (!formState.city.trim()) errors.city = 'City is required.';
    if (!STATUS_OPTIONS.includes(formState.status)) errors.status = 'Status must be ACTIVE or INACTIVE.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setSnackbar({ open: true, message: 'Please correct the validation errors.', severity: 'error' });
      return;
    }
    setSubmitLoading(true);
    try {
      if (isEditing && selectedRecruiter) {
        const payload: UpdateFieldRecruiterRequest = formState;
        await fieldRecruiterService.updateFieldRecruiter(selectedRecruiter.id, payload);
        setSnackbar({ open: true, message: 'Recruiter updated successfully.', severity: 'success' });
      } else {
        await fieldRecruiterService.createFieldRecruiter(formState);
        setSnackbar({ open: true, message: 'Recruiter created successfully.', severity: 'success' });
      }
      handleCloseDialog();
      await loadRecruiters();
    } catch (error: any) {
      console.error('Error saving field recruiter:', error);
      setSnackbar({ open: true, message: error?.response?.data?.message || (isEditing ? 'Failed to update recruiter.' : 'Failed to create recruiter.'), severity: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (recruiter: FieldRecruiter) => {
    if (!window.confirm(`Delete Field Recruiter ${recruiter.name}?`)) return;
    try {
      await fieldRecruiterService.deleteFieldRecruiter(recruiter.id);
      setSnackbar({ open: true, message: 'Recruiter deleted successfully.', severity: 'success' });
      await loadRecruiters();
    } catch (error) {
      console.error('Error deleting field recruiter:', error);
      setSnackbar({ open: true, message: 'Failed to delete recruiter.', severity: 'error' });
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>Field Recruiter Management</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Create, edit, and manage Field Recruiter assignments across cities.</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>Add Field Recruiter</Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Search"
              placeholder="Search by Recruiter ID, Phone or Email"
              value={searchFilter}
              onChange={(event) => {
                setPage(0);
                setSearchFilter(event.target.value);
              }}
              sx={{ width: { xs: '100%', sm: 280 } }}
            />
            <TextField
              select
              SelectProps={{ multiple: true, renderValue: (selected) => (selected as string[]).join(', ') }}
              label="City"
              value={cityFilter}
              onChange={(event) => {
                const value = event.target.value;
                setPage(0);
                setCityFilter(typeof value === 'string' ? value.split(',') : value);
              }}
              sx={{ minWidth: 220 }}
            >
              {cities.map((city) => <MenuItem key={city.id} value={city.name}>{city.displayName || city.name}</MenuItem>)}
            </TextField>
            <TextField select label="Status" value={statusFilter} onChange={(event) => {
              setPage(0);
              setStatusFilter(event.target.value as FieldRecruiterStatus | '');
            }} sx={{ minWidth: 180 }}>
              <MenuItem value="">All statuses</MenuItem>
              {STATUS_OPTIONS.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
            </TextField>
          </Stack>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 'none', overflowX: 'auto' }}>
              <Table>
                <TableHead><TableRow>
                  <TableCell>Employee ID</TableCell><TableCell>Name</TableCell><TableCell>Phone</TableCell><TableCell>Email</TableCell><TableCell>City</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {recruiters.length === 0 ? <TableRow><TableCell colSpan={7} align="center">No Field Recruiters found.</TableCell></TableRow> : recruiters.map((recruiter) => (
                    <TableRow key={recruiter.id} sx={{ '& > td': { py: 1 } }}>
                      <TableCell>{recruiter.employeeId}</TableCell><TableCell>{recruiter.name}</TableCell><TableCell>{recruiter.phone}</TableCell><TableCell>{recruiter.email}</TableCell><TableCell>{recruiter.city}</TableCell><TableCell>{recruiter.status}</TableCell>
                      <TableCell align="right"><Stack direction="row" spacing={1} justifyContent="flex-end"><IconButton size="small" onClick={() => handleOpenDialog(recruiter)}><EditIcon fontSize="small" /></IconButton><IconButton size="small" color="error" onClick={() => handleDelete(recruiter)}><DeleteIcon fontSize="small" /></IconButton></Stack></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onPageChange={(_, newPage) => setPage(newPage)}
          />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{isEditing ? 'Edit Field Recruiter' : 'Add Field Recruiter'}</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Employee ID" value={formState.employeeId} onChange={(event) => handleFormChange('employeeId', event.target.value)} fullWidth error={!!formErrors.employeeId} helperText={formErrors.employeeId} />
          <TextField label="Name" value={formState.name} onChange={(event) => handleFormChange('name', event.target.value)} fullWidth error={!!formErrors.name} helperText={formErrors.name} />
          <TextField label="Phone Number" value={formState.phone} onChange={(event) => handleFormChange('phone', event.target.value)} fullWidth error={!!formErrors.phone} helperText={formErrors.phone} />
          <TextField label="Email" value={formState.email} onChange={(event) => handleFormChange('email', event.target.value)} fullWidth error={!!formErrors.email} helperText={formErrors.email} />
          <FormControl fullWidth error={!!formErrors.city}>
            <InputLabel id="field-recruiter-city-label">City</InputLabel>
            <Select
              labelId="field-recruiter-city-label"
              multiple
              value={selectedCities}
              onChange={handleCityChange}
              input={<OutlinedInput label="City" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => <Chip key={value} label={value} size="small" />)}
                </Box>
              )}
            >
              {citiesLoading ? (
                <MenuItem disabled>Loading cities...</MenuItem>
              ) : citiesLoadError ? (
                <MenuItem disabled>Unable to load cities</MenuItem>
              ) : cities.length === 0 ? (
                <MenuItem disabled>No operational cities available</MenuItem>
              ) : (
                cities.map((city) => <MenuItem key={city.id} value={city.name}>{city.displayName || city.name}</MenuItem>)
              )}
            </Select>
            {formErrors.city && <Typography variant="caption" color="error" sx={{ ml: 1.75 }}>{formErrors.city}</Typography>}
          </FormControl>
          <TextField select label="Status" value={formState.status} onChange={(event) => handleFormChange('status', event.target.value)} fullWidth error={!!formErrors.status} helperText={formErrors.status}>
            {STATUS_OPTIONS.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
          </TextField>
        </Stack></DialogContent>
        <DialogActions><Button onClick={handleCloseDialog}>Cancel</Button><Button disabled={submitLoading} variant="contained" onClick={handleSubmit}>{isEditing ? 'Update' : 'Create'}</Button></DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}>
        <Alert onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))} severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default FieldRecruiterManagement;
